import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import axios from 'axios';
import { ApplyProfileData } from '../../hooks/useEventParticipation';

interface FullProfile {
  fullName:   string;
  furigana?:  string;
  university?: string;
  faculty?:   string;
  phone?:     string;
  birthDate?: string;
  gender?:    string;
  email?:     string;
}

interface EventApplyFormDialogProps {
  open:      boolean;
  onClose:   () => void;
  onSubmit:  (data: ApplyProfileData) => Promise<void>;
  eventTitle: string;
  isSubmitting: boolean;
}

const GENDER_LABELS: Record<string, string> = {
  male:   '男性',
  female: '女性',
  other:  'その他',
};

const REQUIRED_KEYS: (keyof FullProfile)[] = [
  'fullName', 'furigana', 'university', 'faculty', 'phone', 'birthDate', 'gender',
];

const FIELD_LABELS: Record<keyof FullProfile, string> = {
  fullName:   '氏名',
  furigana:   'ふりがな',
  university: '大学',
  faculty:    '学部・学科',
  phone:      '電話番号',
  birthDate:  '生年月日',
  gender:     '性別',
  email:      'メール',
};

const formatBirthDate = (d: string) => {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return `${dt.getFullYear()}/${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}`;
};

export const EventApplyFormDialog: React.FC<EventApplyFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  eventTitle,
  isSubmitting,
}) => {
  const [profile,  setProfile]  = useState<FullProfile | null>(null);
  const [loading,  setLoading]  = useState(false);
  const [form,     setForm]     = useState<ApplyProfileData>({});
  const [errors,   setErrors]   = useState<Partial<Record<keyof ApplyProfileData, string>>>({});

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setLoading(true);
    axios
      .get<{ success: boolean; data: { user: FullProfile } }>('/api/users/profile', { withCredentials: true })
      .then((res) => {
        const p = res.data.data.user;
        setProfile(p);
        setForm({
          fullName:   p.fullName   || '',
          furigana:   p.furigana   || '',
          university: p.university || '',
          faculty:    p.faculty    || '',
          phone:      p.phone      || '',
          birthDate:  p.birthDate  || '',
          gender:     p.gender     || '',
        });
      })
      .catch(() => setForm({}))
      .finally(() => setLoading(false));
  }, [open]);

  const missingKeys = REQUIRED_KEYS.filter((k) => !profile?.[k]);
  const allFilled   = profile !== null && missingKeys.length === 0;

  const handleChange = (key: keyof ApplyProfileData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const errs: typeof errors = {};
    missingKeys.forEach((k) => {
      if (k === 'gender') {
        if (!form.gender) errs.gender = '性別を選択してください';
      } else {
        if (!form[k as keyof ApplyProfileData]?.trim()) errs[k as keyof ApplyProfileData] = `${FIELD_LABELS[k]}は必須です` as any;
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!allFilled && !validate()) return;
    await onSubmit(form);
  };

  const isMissing = (k: keyof FullProfile) => missingKeys.includes(k);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>参加申込フォーム</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{eventTitle}</Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : allFilled ? (
          /* ── 全項目入力済み: 確認ビュー ── */
          <Box>
            <Alert severity="success" sx={{ mb: 2.5 }}>
              登録済みの情報で申し込みます。内容をご確認ください。
            </Alert>
            <Grid container spacing={1.5}>
              {REQUIRED_KEYS.map((k) => (
                <Grid item xs={12} sm={6} key={k}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      {FIELD_LABELS[k]}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {k === 'gender'
                        ? GENDER_LABELS[profile![k] as string] ?? profile![k]
                        : k === 'birthDate'
                        ? formatBirthDate(profile![k] as string)
                        : profile![k] as string}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          /* ── 未入力項目あり: 入力フォーム ── */
          <Box component="form" noValidate>
            <Alert severity="info" sx={{ mb: 2.5 }}>
              以下の未入力項目を補完してから申込してください。
              入力した内容はプロフィールにも保存され、次回以降この画面に表示されなくなります。
            </Alert>

            <Grid container spacing={2}>
              {isMissing('fullName') && (
                <Grid item xs={12} sm={6}>
                  <TextField label="氏名" required fullWidth
                    value={form.fullName ?? ''}
                    onChange={handleChange('fullName')}
                    error={!!errors.fullName} helperText={errors.fullName}
                  />
                </Grid>
              )}
              {/* 氏名が登録済みでふりがなのみ未入力の場合、氏名をリードオンリーでペア表示 */}
              {!isMissing('fullName') && isMissing('furigana') && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="氏名（登録済み）"
                    fullWidth
                    value={profile?.fullName ?? ''}
                    disabled
                    InputProps={{ readOnly: true }}
                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.6)' } }}
                  />
                </Grid>
              )}
              {isMissing('furigana') && (
                <Grid item xs={12} sm={6}>
                  <TextField label="ふりがな" required fullWidth
                    value={form.furigana ?? ''}
                    onChange={handleChange('furigana')}
                    error={!!errors.furigana} helperText={errors.furigana}
                    placeholder="やまだ たろう"
                  />
                </Grid>
              )}
              {isMissing('university') && (
                <Grid item xs={12} sm={6}>
                  <TextField label="大学" required fullWidth
                    value={form.university ?? ''}
                    onChange={handleChange('university')}
                    error={!!errors.university} helperText={errors.university}
                  />
                </Grid>
              )}
              {isMissing('faculty') && (
                <Grid item xs={12} sm={6}>
                  <TextField label="学部・学科" required fullWidth
                    value={form.faculty ?? ''}
                    onChange={handleChange('faculty')}
                    error={!!errors.faculty} helperText={errors.faculty}
                  />
                </Grid>
              )}
              {isMissing('phone') && (
                <Grid item xs={12} sm={6}>
                  <TextField label="電話番号" required fullWidth
                    value={form.phone ?? ''}
                    onChange={handleChange('phone')}
                    error={!!errors.phone} helperText={errors.phone}
                    inputProps={{ inputMode: 'tel' }}
                  />
                </Grid>
              )}
              {isMissing('birthDate') && (
                <Grid item xs={12} sm={6}>
                  <TextField label="生年月日" required fullWidth type="date"
                    InputLabelProps={{ shrink: true }}
                    value={form.birthDate ?? ''}
                    onChange={handleChange('birthDate')}
                    error={!!errors.birthDate} helperText={errors.birthDate}
                  />
                </Grid>
              )}
              {isMissing('gender') && (
                <Grid item xs={12}>
                  <FormControl required error={!!errors.gender}>
                    <FormLabel sx={{ fontWeight: 600, mb: 0.5 }}>性別</FormLabel>
                    <RadioGroup row value={form.gender ?? ''} onChange={handleChange('gender')}>
                      {Object.entries(GENDER_LABELS).map(([val, label]) => (
                        <FormControlLabel key={val} value={val} control={<Radio />} label={label} />
                      ))}
                    </RadioGroup>
                    {errors.gender && (
                      <Typography variant="caption" color="error">{errors.gender}</Typography>
                    )}
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>キャンセル</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || loading}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          {isSubmitting ? '申込中...' : '申込確定'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
